import React from "react";
import _ from "lodash";
import { FormattedMessage } from "react-intl";
import { Action } from "typescript-fsa";

import { ApiButton } from "@api/components/ApiButton";
import { messages as dspsMessages } from "@dsps/messages";
import { messages as musicMessages } from "@music/messages";
import { Album } from "@music/models";
import { messages as uiMessages } from "@ui/messages";
import { DspSelect } from "@dsps/components/DspSelect";
import { AlbumSelect } from "@music/components/AlbumSelect";
import { TypeSelect } from "../TypeSelect";
import { StatusTypeSelect } from "../StatusTypeSelect";
import { messages } from "../../messages";
import { Filters } from "../../models";

interface DeliveriesFiltersProps {
    actionCreator: (filters: Filters) => Action<any>;
    onFilter?: (filters: Filters) => void;
    filters?: Filters;
}

interface DeliveriesFiltersState {
    filters: Filters;
    dirty: boolean;
    lastFilters: Filters;
}

const defaultFilters: Filters = {
    types: [],
    statuses: [],
    dspIds: [],
    albumIds: [],
};

class DeliveriesFilters extends React.Component<DeliveriesFiltersProps, DeliveriesFiltersState> {
    constructor(props: DeliveriesFiltersProps) {
        super(props);

        const filters = props.filters != null ? props.filters : defaultFilters;
        this.state = {
            filters,
            dirty: false,
            lastFilters: filters,
        };
    }

    private onAlbumChange(albums: Album | Album[]) {
        const { filters } = this.state;
        let albumIds = [];
        if (!Array.isArray(albums)) {
            albumIds = [albums.id];
        }
        else {
            albumIds = albums.map((a: Album) => a.id);
        }
        this.setState({
            filters: { ...filters, albumIds },
            dirty: true,
        });
    }

    private onTypeChange(types: string | string[]) {
        const { filters } = this.state;
        if (!Array.isArray(types)) {
            types = [types];
        }
        this.setState({
            filters: { ...filters, types },
            dirty: true,
        });
    }

    private onStatusTypeChange(statuses: string | string[]) {
        const { filters } = this.state;
        if (!Array.isArray(statuses)) {
            statuses = [statuses];
        }
        this.setState({
            filters: { ...filters, statuses },
            dirty: true,
        });
    }

    private onDspChange(dspIds: number | number[]) {
        const { filters } = this.state;
        if (!Array.isArray(dspIds)) {
            dspIds = [dspIds];
        }
        this.setState({
            filters: { ...filters, dspIds },
            dirty: true,
        });
    }

    private onFilter() {
        const { actionCreator, onFilter } = this.props;
        const { filters } = this.state;
        if (onFilter) {
            onFilter(filters);
        }
        this.setState({
            dirty: false,
            lastFilters: filters,
        });
        return actionCreator(filters);
    }

    private resetFilter() {
        const { actionCreator, onFilter } = this.props;
        const shouldDispatch = !_.isEqual(this.state.lastFilters, defaultFilters);
        this.setState({
            filters: defaultFilters,
            dirty: false,
            lastFilters: defaultFilters,
        });
        if (onFilter) {
            onFilter(defaultFilters);
        }
        if (shouldDispatch) {
            return actionCreator(defaultFilters);
        }
        return { type: "NULL", payload: null };
    }

    public render() {
        const { dirty, filters } = this.state;
        const { types, statuses, dspIds, albumIds } = this.state.filters;

        const filterDisabled = !dirty;
        const resetDisabled = _.isEqual(filters, defaultFilters);

        return (
            <div className="deliveries-filters">
                <div className="deliveries-filters__album-select">
                    <div>
                        <label htmlFor="deliveries-filters-album-select">
                            <FormattedMessage {...musicMessages.filterAlbums} />
                        </label>
                        <AlbumSelect
                            multiple={true}
                            selected={albumIds}
                            onChange={(album: Album | Album[]) => this.onAlbumChange(album)}
                            id="deliveries-filters-album-select"
                        />
                    </div>
                </div>
                <div className="deliveries-filters__type-select">
                    <label htmlFor="deliveries-filters-type-select">
                        <FormattedMessage {...messages.filterTypes} />
                    </label>
                    <TypeSelect
                        multiple={true}
                        selected={types}
                        onChange={(name) => this.onTypeChange(name)}
                        id="deliveries-filters-type-select"
                    />
                </div>
                <div className="deliveries-filters__dsp-select">
                    <label htmlFor="deliveries-filters-dsp-select">
                        <FormattedMessage {...dspsMessages.filterDsps} />
                    </label>
                    <DspSelect
                        multiple={true}
                        selected={dspIds}
                        onChange={(id) => this.onDspChange(id)}
                        id="deliveries-filters-dsp-select"
                    />
                </div>
                <div className="deliveries-filters__status-type-select">
                    <label htmlFor="deliveries-filters-status-type-select">
                        <FormattedMessage {...messages.filterStatusTypes} />
                    </label>
                    <StatusTypeSelect
                        multiple={true}
                        selected={statuses}
                        onChange={(name) => this.onStatusTypeChange(name)}
                        id="deliveries-filters-status-type-select"
                    />
                </div>
                <div className="deliveries-filters__controls">
                    <ApiButton
                        noOutline
                        color="primary"
                        disabled={filterDisabled}
                        className="deliveries-filters__filter-button"
                        actionCreators={[() => this.onFilter()]}
                    >
                        <FormattedMessage {...uiMessages.filter} />
                    </ApiButton>
                    <ApiButton
                        disabled={resetDisabled}
                        className="deliveries-filters__reset-button"
                        actionCreators={[() => this.resetFilter()]}
                    >
                        <FormattedMessage {...uiMessages.resetFilters} />
                    </ApiButton>
                </div>
            </div>
        );
    }
}

export {
    DeliveriesFilters,
    DeliveriesFiltersProps,
    DeliveriesFiltersState,
    defaultFilters,
};
